import React from 'react';
import { C, FONT_BODY, CARD_STYLE } from '../theme';
import { CITATIONS, EVIDENCE_LABEL, EVIDENCE_VERSION, VOLUME_LANDMARKS } from '../science';

const TIER_LABEL = { 1:'Umbrella review', 2:'Meta-analisi', 3:'RCT / Studio', 5:'Opinione esperto' };

export default function SciencePage({ onBack }) {
  const citationList = Object.entries(CITATIONS);
  const landmark_entries = Object.entries(VOLUME_LANDMARKS);

  return (
    <div style={{ fontFamily: FONT_BODY, padding: '0 0 32px' }}>

      {/* Header */}
      {onBack && (
        <button onClick={onBack}
          style={{ background:'none', border:'none', cursor:'pointer', padding:'0 0 16px',
            fontSize:15, color:C.blue, fontFamily:FONT_BODY, display:'flex', alignItems:'center', gap:6 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M15 6l-6 6 6 6" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Indietro
        </button>
      )}

      <div style={{ fontSize:22, fontWeight:700, color:C.text, letterSpacing:-0.5, marginBottom:6 }}>
        Come analizza FitProg
      </div>
      <div style={{ fontSize:13, color:C.subtle, marginBottom:24 }}>
        Evidence base aggiornata a: {EVIDENCE_VERSION}
      </div>

      {/* Evidence tiers legend */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.subtle, letterSpacing:0.4,
          textTransform:'uppercase', marginBottom:10 }}>
          Scala di evidenza
        </div>
        {[
          { range:'80-100', label:'Forte', desc:'2+ meta-analisi concordanti' },
          { range:'60-79',  label:'Moderato', desc:'1 meta-analisi o RCT replicato' },
          { range:'40-59',  label:'Basso', desc:'1 RCT o studio osservazionale' },
          { range:'20-39',  label:'Euristica', desc:'Opinione esperto + coerenza pratica' },
          { range:'0-19',   label:'Non supportato', desc:'Soglia arbitraria senza fonte' },
        ].map(({ range, label, desc }) => (
          <div key={range} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <span style={{ fontSize:13, fontWeight:700, color:C.subtle, width:52, flexShrink:0 }}>
              {range}
            </span>
            <span style={{ fontSize:14, fontWeight:600, color:C.text, width:100, flexShrink:0 }}>
              {label}
            </span>
            <span style={{ fontSize:13, color:C.subtle }}>{desc}</span>
          </div>
        ))}
      </div>

      {/* Citations table */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.subtle, letterSpacing:0.4,
          textTransform:'uppercase', marginBottom:10 }}>
          Fonti utilizzate
        </div>
        <div style={{ ...CARD_STYLE, padding:0, overflow:'hidden' }}>
          {citationList.map(([key, cit], i) => (
            <div key={key}>
              <div style={{ padding:'12px 16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:C.white,
                    background: cit.score >= 80 ? C.green : cit.score >= 60 ? C.blue : cit.score >= 40 ? C.amber : C.subtle,
                    borderRadius:6, padding:'2px 7px', flexShrink:0 }}>
                    {cit.score}/100
                  </span>
                  <span style={{ fontSize:12, color:C.subtle }}>
                    {EVIDENCE_LABEL(cit.score)} · {TIER_LABEL[cit.tier] || `T${cit.tier}`}
                  </span>
                </div>
                <div style={{ fontSize:13, color:C.text, lineHeight:1.5 }}>{cit.text}</div>
                {(cit.doi || cit.pubmed) && (
                  <div style={{ fontSize:11, color:C.subtle, marginTop:3 }}>
                    {cit.doi && `DOI: ${cit.doi}`}
                    {cit.pubmed && ` PubMed: ${cit.pubmed}`}
                  </div>
                )}
              </div>
              {i < citationList.length - 1 && (
                <div style={{ height:'0.5px', background:'rgba(60,60,67,0.10)', margin:'0 16px' }}/>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Volume landmarks */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.subtle, letterSpacing:0.4,
          textTransform:'uppercase', marginBottom:10 }}>
          Volume settimanale per muscolo (serie efficaci)
        </div>
        <div style={{ ...CARD_STYLE, padding:0, overflow:'hidden' }}>
          {landmark_entries.map(([muscle, lm], i) => (
            <div key={muscle}>
              <div style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <span style={{ fontSize:14, fontWeight:600, color:C.text, textTransform:'capitalize' }}>
                    {muscle === 'default' ? 'Tutti i muscoli (default)' : muscle}
                  </span>
                  {lm.expertEstimate && (
                    <span style={{ fontSize:11, color:C.subtle, marginLeft:8 }}>
                      (stima esperto)
                    </span>
                  )}
                </div>
                <div style={{ display:'flex', gap:12, flexShrink:0, fontSize:12 }}>
                  <span><span style={{ color:C.subtle }}>MV </span><strong>{lm.mv}</strong></span>
                  <span><span style={{ color:C.subtle }}>MEV </span><strong>{lm.mev}</strong></span>
                  <span><span style={{ color:C.subtle }}>MAV </span><strong>{lm.mav_lo}-{lm.mav_hi}</strong></span>
                  <span><span style={{ color:C.subtle }}>MRV </span><strong>{lm.mrv}</strong></span>
                </div>
              </div>
              {i < landmark_entries.length - 1 && (
                <div style={{ height:'0.5px', background:'rgba(60,60,67,0.10)', margin:'0 16px' }}/>
              )}
            </div>
          ))}
        </div>
        <div style={{ fontSize:12, color:C.subtle, marginTop:8, lineHeight:1.5 }}>
          MV = volume minimo per mantenere la massa. MEV = volume minimo per crescere.
          MAV = range ottimale. MRV = massimo recuperabile.
          I range generali MV/MEV/MAV sono supportati da meta-analisi (Bernandez-Vazquez 2022, Schoenfeld 2017).
          I valori specifici per muscolo sono stime esperto (Israetel 2021, non peer-reviewed).
        </div>
      </div>

      {/* Euristiche disclosure */}
      <div>
        <div style={{ fontSize:13, fontWeight:600, color:C.subtle, letterSpacing:0.4,
          textTransform:'uppercase', marginBottom:10 }}>
          Cosa e' una euristica
        </div>
        <div style={{ ...CARD_STYLE, padding:'14px 16px', fontSize:14, color:C.text, lineHeight:1.6 }}>
          Alcune regole dell'app (es. soglie di deload, rilevamento stallo, fatica) non hanno una fonte
          peer-reviewed specifica. Sono etichettate come "Euristica" (score 20-39): hanno senso pratico
          ma non sono state validate in studi controllati. Usale come indicazioni, non come prescrizioni
          cliniche.
        </div>
      </div>

    </div>
  );
}
